import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  useAuth,
  useBalances,
  useCheckoutPage,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useQueryString,
} from "../../hooks";
import {
  ICheckoutClosedEventRequest,
  IViewHostedPaymentLink,
} from "../../interfaces-and-types";

import { View } from "./View";
// import { transactionType } from "../../enums";

export type TPaymentMethodName = "card" | "gateway";

interface IHostedPaymentLinksProps {
  paymentLinkToPreview?: IViewHostedPaymentLink;
  isPreviewMode: boolean;

  hostedLinkUrl?: string; // added to allow displaying top-up payment links
  closeModal?: () => void;
}

export interface IHostedPaymentLinkViewProps {
  isPreviewMode: boolean;

  isCancelButtonHidden: boolean;

  searchLineItemsText: string | null;
  setSearchLineItemsText: (value: string | null) => void;

  isSuccessHostedPaymentLink: boolean;
  isOrderCompletePageVisible: boolean;
  isPaymentFailedPageVisible: boolean;
  hostedPaymentLink: IViewHostedPaymentLink | undefined;
  paymentMethodName: TPaymentMethodName;
  tx_ref: string | null;
  // tx_type: transactionType | null;
  iframeUrl: string;
  isIframeOpen: boolean;
  paymentMethodSwitcherVisible: boolean;
  sendCheckoutClosedEvent: (tx_ref: string) => void;
  setPaymentMethodName: (name: TPaymentMethodName) => void;
  showSuccessPage: () => void;
  openInIframe: (url: string) => void;
  closeIframe: () => void;
  closeIframeShowSuccessMessage: () => void;
  closeIframeShowFailedMessage: () => void;
}

/**
 * This displays a checkout page for customers to complete payment using various methods
 * TODO: change the frontend view to abandoned after 24 hours if payment has not been completed to match backend behaviour
 * @param props properties that allow using the component for previewing during payment link creation
 * @returns
 */
export const HostedPaymentLinks = (props: IHostedPaymentLinksProps) => {
  const { isPreviewMode, paymentLinkToPreview, hostedLinkUrl, closeModal } =
    props;

  const queryClient = useQueryClient();

  const { setApiKey, setCheckoutSessionId } = useAuth();

  const { refreshBalances } = useBalances();

  const [iframeUrl, setIframeUrl] = useState("");
  const [isIframeOpen, setIsIframeOpen] = useState(false);

  const [isOrderCompletePageVisible, setOrderCompletePageVisible] =
    useState(false);

  const [isPaymentFailedPageVisible, setPaymentFailedPageVisible] =
    useState(false);

  const { mutate: sendCheckoutClosedEvent } = useMerchantApiMutation<
    ICheckoutClosedEventRequest,
    void
  >("sendCheckoutClosedEvent").usePostMutation;

  const showSuccessPage = () => setOrderCompletePageVisible(true);
  const showFailedPage = () => setPaymentFailedPageVisible(true);

  const queryString = useQueryString();
  const hostedLinkUrlQueryString =
    hostedLinkUrl || queryString.get("hostedLinkUrl");

  const {
    isSuccess: isSuccessHostedPaymentLink,
    data: hostedPaymentLinkFromApi,
  } = useMerchantApiQueryWithParams<
    { linkUrl: string },
    IViewHostedPaymentLink
  >(
    [
      "getHostedPaymentLink",
      {
        linkUrl: hostedLinkUrlQueryString || "",
      },
    ],
    {
      enabled: !isPreviewMode && hostedLinkUrlQueryString !== null,
      refetchInterval: false,
      retry: 3,
    }
  );

  // if called from unauthenticated endpoint, set the public API key
  useEffect(() => {
    if (isSuccessHostedPaymentLink) {
      if (
        !!hostedPaymentLinkFromApi &&
        hostedPaymentLinkFromApi.merchant_public_key
      ) {
        setApiKey(hostedPaymentLinkFromApi.merchant_public_key);
        setCheckoutSessionId(hostedPaymentLinkFromApi.checkout_session_id);
      } else
        alert(
          "Could not retrieve payment link details: Please reload the page"
        );
    }
  }, [
    isSuccessHostedPaymentLink,
    hostedPaymentLinkFromApi?.merchant_public_key,
    hostedPaymentLinkFromApi?.checkout_session_id,
  ]);

  const hostedPaymentLink = isPreviewMode
    ? paymentLinkToPreview
    : hostedPaymentLinkFromApi;

  const [paymentMethodName, setPaymentMethodName] =
    useState<TPaymentMethodName>(
      hostedPaymentLink?.show_payment_providers ? "gateway" : "card"
    );

  useEffect(() => {
    setPaymentMethodName(
      hostedPaymentLink?.show_payment_providers ? "gateway" : "card"
    );
  }, [hostedPaymentLink?.show_payment_providers]);

  const openInIframe = (url: string) => {
    setIframeUrl(url);
    setIsIframeOpen(true);
  };

  const closeIframe = () => {
    if (
      window.confirm(
        "Are you sure you want to close this view? Your payment has not been confirmed!!!"
      )
    )
      setIsIframeOpen(false);
  };

  const closeIframeShowSuccessMessage = () => {
    setIsIframeOpen(false);
    showSuccessPage();

    if (hostedPaymentLink?.is_top_up_payment_link) {
      refreshBalances();
      queryClient.refetchQueries(["getTopUps"]);

      setTimeout(() => closeModal && closeModal(), 5000);
    }
  };

  const closeIframeShowFailedMessage = () => {
    setIsIframeOpen(false);
    showFailedPage();
  };

  const {
    searchLineItemsText,
    setSearchLineItemsText,
    paymentMethodSwitcherVisible,
  } = useCheckoutPage();

  const isCancelButtonHidden =
    isOrderCompletePageVisible ||
    isPaymentFailedPageVisible ||
    isPreviewMode ||
    hostedPaymentLink?.is_top_up_payment_link ||
    isIframeOpen;

  return (
    <View
      isPreviewMode={isPreviewMode}
      isCancelButtonHidden={isCancelButtonHidden}
      searchLineItemsText={searchLineItemsText}
      setSearchLineItemsText={setSearchLineItemsText}
      isSuccessHostedPaymentLink={isPreviewMode || isSuccessHostedPaymentLink}
      isOrderCompletePageVisible={isOrderCompletePageVisible}
      isPaymentFailedPageVisible={isPaymentFailedPageVisible}
      paymentMethodSwitcherVisible={paymentMethodSwitcherVisible}
      hostedPaymentLink={hostedPaymentLink}
      paymentMethodName={paymentMethodName}
      tx_ref={hostedPaymentLink?.tx_ref || null}
      iframeUrl={iframeUrl}
      isIframeOpen={isIframeOpen}
      sendCheckoutClosedEvent={async (tx_ref) =>
        await sendCheckoutClosedEvent({ tx_ref })
      }
      setPaymentMethodName={setPaymentMethodName}
      showSuccessPage={showSuccessPage}
      openInIframe={openInIframe}
      closeIframe={closeIframe}
      closeIframeShowSuccessMessage={closeIframeShowSuccessMessage}
      closeIframeShowFailedMessage={closeIframeShowFailedMessage}
    />
  );
};
